import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

/**
 * Calendar -> Week -> By team (docs/TEAM_TOURNAMENTS.md §4).
 * Rows = the season's teams, columns = the 7 days. Each card is ONE team's game
 * (time · opponent · court · round). Tournament days get a trophy header and
 * "+ Hold slot". A card opens a per-game editor; dragging a card to another day
 * in the same row changes its date. Every write goes to the per-game endpoints
 * (routes/team_tournaments.py), which use the shared game paths (GAME_SYNC.md).
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const pad = (n) => String(n).padStart(2, '0');
const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const minToLabel = (m) => { let h = Math.floor(m / 60); const mm = m % 60; const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12; return `${h}:${pad(mm)} ${ap}`; };
const STATE_LABEL = { '': 'Game', held: 'Slot held', if_win: 'If we win' };
// A held slot's stand-in opponent ("Pool slot 2", made by the hold endpoint) is not a real opponent.
const isPlaceholderOpp = (n) => /^Pool slot \d+$/i.test((n || '').trim());
// Stand-in opponent of a tournament game: empty, "Pool slot N", or one of the tournament's placeholders —
// matched by the opponent RECORD (slug) first, so a second spelling of the same placeholder still counts.
const isStandIn = (n, t, slug) => {
  const s = (n || '').trim();
  return !s || isPlaceholderOpp(s) || !!(t && ((slug && (t.placeholder_opponent_slugs || []).includes(slug))
    || s === t.name || s === t.short_name || (t.placeholder_opponents || []).includes(s)));
};

// The org's venues (one entry per slug) for the court dropdown.
function useVenues(orgId) {
  const [venues, setVenues] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/game-schedule/${orgId}/venues`)
      .then(({ data }) => {
        const seen = new Set();
        setVenues((data?.venues_full || []).filter(v => v.slug && !seen.has(v.slug) && seen.add(v.slug))
          .map(v => ({ slug: v.slug, name: v.name })).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => setVenues([]));
  }, [orgId]);
  return venues;
}

/**
 * One team's game, add / edit / delete — the SAME dialog from Calendar → Week →
 * By team and from the List view's team cells (TEAM_TOURNAMENTS.md §9). `o` is a
 * card: {gameId | isNew, teamSlug, teamLabel, date, startMin, opponent, venueSlug,
 * round, slotState, tournamentId, homeAway, published}.
 */
export function GameEditDialog({ o, orgId, sport, season, tournaments, opponents, onAddOpponent, isDark, readOnly, onClose, onDone }) {
  const venues = useVenues(orgId);
  const [err, setErr] = useState('');
  // The game's current score (GAME_SYNC.md §8a) — loaded fresh so the editor never shows a stale copy.
  const [score, setScore] = useState(o.isNew ? {} : null);
  useEffect(() => {
    if (o.isNew || !o.gameId) return;
    axios.get(`${API_URL}/api/game-schedule/${orgId}/games/${o.gameId}`)
      .then(r => setScore(r.data.game || {})).catch(() => setScore({}));
  }, [orgId, o.gameId, o.isNew]);
  const run = async (fn) => {
    setErr('');
    try { await fn(); onDone?.(); onClose?.(); }
    catch (e) { setErr(e?.response?.data?.detail || e?.message || 'Save failed'); }
  };
  return (
    <GameEditor key={score ? 'loaded' : 'loading'} o={o} score={score} venues={venues} tournaments={tournaments} isDark={isDark} readOnly={readOnly} error={err}
      opponents={opponents} onAddOpponent={onAddOpponent} onClose={onClose}
      onSave={(body) => run(() => (o.isNew
        ? axios.post(`${API_URL}/api/game-schedule/${orgId}/games`,
          { tournament_id: o.tournamentId || '', ...body, sport, season, team_slug: o.teamSlug })
        : axios.patch(`${API_URL}/api/game-schedule/${orgId}/games/${o.gameId}`, body)))}
      onDelete={() => run(() => axios.delete(`${API_URL}/api/game-schedule/${orgId}/games/${o.gameId}`))} />
  );
}

export default function TeamWeekView({ days, occByDate, teamColumns, tournaments, teamFilter, isDark, readOnly,
                                       orgId, sport, season, pillColor, onChanged, opponents = [], onAddOpponent }) {
  const [edit, setEdit] = useState(null);       // occurrence being edited
  const [tEdit, setTEdit] = useState(null);     // tournament being edited ({} = new)
  const [err, setErr] = useState('');
  const venues = useVenues(orgId);
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const line = isDark ? 'border-slate-700' : 'border-slate-200';
  const head = isDark ? 'bg-slate-800' : 'bg-slate-50';

  const rows = useMemo(() => (teamColumns || []).filter(c => teamFilter === 'all' || !teamFilter || c.slug === teamFilter),
    [teamColumns, teamFilter]);
  const tournOn = (ds) => (tournaments || []).find(t => t.start_date <= ds && ds <= t.end_date);
  // A card shows the game's real opponent, exactly as the List and the editor do (AJ 2026-10-18).
  const oppLabel = (o) => {
    const n = (o.opponent || '').trim();
    if (!n) return 'TBD';
    return n;
  };
  // "Rocky Top Sports World - Dominos Court" -> "Dominos Court" (the venue name's own last part).
  const courtLabel = (v) => (v || '').split(' - ').pop();

  const cardsFor = (teamSlug, ds) => (occByDate[ds] || [])
    .filter(o => o.type === 'game' && o.teamSlug === teamSlug)
    .sort((a, b) => (a.startMin ?? 1e9) - (b.startMin ?? 1e9));

  const run = async (fn) => {
    setErr('');
    try { await fn(); onChanged?.(); return true; }
    catch (e) { setErr(e?.response?.data?.detail || e?.message || 'Save failed'); return false; }
  };
  const hold = (t, teamSlug, ds) => run(() => axios.post(`${API_URL}/api/game-schedule/${orgId}/tournaments/${t.id}/hold`,
    { team_slug: teamSlug, date: ds }));
  const patchGame = (gameId, body) => run(() => axios.patch(`${API_URL}/api/game-schedule/${orgId}/games/${gameId}`, body));

  const onDragStart = (o) => (e) => { e.dataTransfer.setData('text/ol-game', JSON.stringify({ id: o.gameId, team: o.teamSlug })); };
  const onDrop = (teamSlug, ds) => (e) => {
    e.preventDefault();
    try {
      const { id, team } = JSON.parse(e.dataTransfer.getData('text/ol-game') || '{}');
      if (id && team === teamSlug) patchGame(id, { date: ds });
    } catch { /* not a game card */ }
  };

  const cardStyle = (o) => o.slotState === 'held' ? `border-dashed border-2 bg-transparent ${muted}`
    : o.slotState === 'if_win' ? 'border-dotted border-2 border-amber-500 bg-amber-50 text-amber-900'
    : pillColor(o);

  return (
    <div className="space-y-2" data-testid="cal-team-week">
      {err && <div className="text-xs rounded px-3 py-2 bg-red-50 text-red-700 border border-red-200" data-testid="tw-error">{err}</div>}
      {!readOnly && (
        <div className="flex items-center gap-2 text-xs">
          <span className={muted}>Tournaments:</span>
          {(tournaments || []).map(t => (
            <button key={t.id} type="button" onClick={() => setTEdit(t)} data-testid={`tw-tourn-${t.id}`}
              className="px-2 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-900">🏆 {t.short_name || t.name}</button>
          ))}
          <button type="button" onClick={() => setTEdit({})} className={`px-2 py-0.5 rounded border ${line}`} data-testid="tw-tourn-new">+ New tournament</button>
        </div>
      )}
      <div className={`border rounded-lg overflow-auto max-h-[74vh] ${line}`}>
        <div className="grid min-w-[980px]" style={{ gridTemplateColumns: `120px repeat(${days.length}, minmax(120px, 1fr))` }}>
          <div className={`sticky left-0 z-10 border-b border-r ${line} ${head}`} />
          {days.map(dt => {
            const ds = toDateStr(dt); const t = tournOn(ds);
            return (
              <div key={ds} className={`text-center py-1.5 border-b border-r ${line} ${head}`}>
                <div className={`text-[11px] ${muted}`}>{DAY_NAMES[dt.getDay()]}</div>
                <div className={`text-sm font-semibold inline-flex items-center justify-center rounded-full w-7 h-7 ${t ? 'bg-amber-500 text-white' : ''}`}>{dt.getDate()}</div>
                {t && <div className={`text-[10.5px] ${muted} px-1 truncate`}>🏆 {t.short_name || t.name}</div>}
              </div>
            );
          })}
          {rows.map(col => {
            const counts = days.reduce((acc, dt) => {
              cardsFor(col.slug, toDateStr(dt)).forEach(o => { if (o.slotState) acc.h += 1; else acc.g += 1; });
              return acc;
            }, { g: 0, h: 0 });
            return (
              <React.Fragment key={col.slug}>
                <div className={`sticky left-0 z-10 px-2 py-2 border-b border-r ${line} ${head} flex flex-col justify-center`}>
                  <span className="font-semibold text-sm">{col.label}</span>
                  <span className={`text-[11px] ${muted}`}>{counts.g} games{counts.h ? ` · ${counts.h} held` : ''}</span>
                </div>
                {days.map(dt => {
                  const ds = toDateStr(dt); const t = tournOn(ds); const cards = cardsFor(col.slug, ds);
                  return (
                    <div key={ds} onDragOver={(e) => e.preventDefault()} onDrop={readOnly ? undefined : onDrop(col.slug, ds)}
                      className={`p-1 border-b border-r ${line} flex flex-col gap-1 min-h-[56px]`} data-testid={`tw-cell-${col.slug}-${ds}`}>
                      {cards.map(o => (
                        <button key={o.id} type="button" draggable={!readOnly && !!o.gameId} onDragStart={onDragStart(o)}
                          onClick={() => o.gameId && setEdit(o)} data-testid={`tw-card-${o.gameId || o.id}`}
                          title={[o.opponent, o.venue, o.round].filter(Boolean).join(' · ')}
                          className={`text-left px-1.5 py-1 rounded border text-[11px] leading-snug ${cardStyle(o)}`}>
                          <div className="font-semibold whitespace-normal break-words">
                            {o.homeAway === 'home' ? 'vs' : '@'} {oppLabel(o)}
                          </div>
                          <div className="opacity-80">
                            {o.startMin != null ? minToLabel(o.startMin) : 'TBD'}{o.venue ? ` · ${courtLabel(o.venue)}` : ''}{o.round ? ` · ${o.round}` : ''}
                            {o.slotState ? ` · ${STATE_LABEL[o.slotState]}` : ''}
                          </div>
                        </button>
                      ))}
                      {!readOnly && (
                        <div className="flex gap-1 mt-auto">
                          <button type="button" data-testid={`tw-add-${col.slug}-${ds}`}
                            onClick={() => setEdit({ isNew: true, teamSlug: col.slug, teamLabel: col.label, date: ds, startMin: null,
                              homeAway: 'away', tournamentId: t && (!(t.schedule_slugs || []).length || t.schedule_slugs.includes(col.schedule_slug)) ? t.id : '' })}
                            className={`flex-1 text-[11px] rounded border border-dashed ${line} ${muted} py-0.5 hover:border-blue-500 hover:text-blue-700`}>+ Game</button>
                          {t && (!(t.schedule_slugs || []).length || t.schedule_slugs.includes(col.schedule_slug)) && (
                            <button type="button" onClick={() => hold(t, col.slug, ds)} data-testid={`tw-hold-${col.slug}-${ds}`}
                              className={`flex-1 text-[11px] rounded border border-dashed ${line} ${muted} py-0.5 hover:border-amber-500 hover:text-amber-700`}>+ Hold</button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      {rows.length === 0 && <div className={`text-sm ${muted} py-6 text-center`}>No teams for this sport and season yet.</div>}
      {edit && <GameEditDialog o={edit} orgId={orgId} sport={sport} season={season} tournaments={tournaments}
        opponents={opponents} onAddOpponent={onAddOpponent} isDark={isDark} readOnly={readOnly}
        onClose={() => setEdit(null)} onDone={onChanged} />}
      {tEdit && <TournamentEditor t={tEdit} teamColumns={teamColumns} venues={venues} isDark={isDark} sport={sport} season={season}
        onClose={() => setTEdit(null)}
        onSave={async (body) => {
          const ok = await run(() => tEdit.id
            ? axios.put(`${API_URL}/api/game-schedule/${orgId}/tournaments/${tEdit.id}`, body)
            : axios.post(`${API_URL}/api/game-schedule/${orgId}/tournaments`, body));
          if (ok) setTEdit(null);
        }}
        onTag={async () => { if (await run(() => axios.post(`${API_URL}/api/game-schedule/${orgId}/tournaments/${tEdit.id}/games`, { all_in_dates: true }))) setTEdit(null); }}
        onDelete={async () => { if (await run(() => axios.delete(`${API_URL}/api/game-schedule/${orgId}/tournaments/${tEdit.id}`))) setTEdit(null); }} />}
    </div>
  );
}

function Shell({ title, onClose, isDark, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-4 overflow-auto" onClick={onClose}>
      <div className={`w-full max-w-md rounded-lg border p-4 space-y-3 mt-10 ${isDark ? 'bg-slate-900 border-slate-700 text-slate-100' : 'bg-white border-slate-200'}`}
        onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <div className="flex items-center justify-between"><h3 className="font-semibold">{title}</h3>
          <button type="button" onClick={onClose} className="text-sm opacity-70 hover:opacity-100">Close</button></div>
        {children}
      </div>
    </div>
  );
}

const inputCls = (isDark) => `w-full rounded border px-2 py-1.5 text-sm ${isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-300'}`;

const ADD_OPP = '__add_opponent__';
function GameEditor({ o, score, venues, tournaments, isDark, readOnly, error, opponents = [], onAddOpponent, onClose, onSave, onDelete }) {
  const t = (tournaments || []).find(x => x.id === o.tournamentId);
  const courts = useMemo(() => {
    const first = new Set(t?.venue_slugs || []);
    return [...venues.filter(v => first.has(v.slug)), ...venues.filter(v => !first.has(v.slug))];
  }, [venues, t]);
  const [f, setF] = useState({
    date: o.date, time: o.startMin != null ? `${pad(Math.floor(o.startMin / 60))}:${pad(o.startMin % 60)}` : '',
    opponent: isPlaceholderOpp(o.opponent) ? '' : (o.opponent || ''), venue_slug: o.venueSlug || '', round: o.round || '',
    slot_state: o.slotState || '', home_away: o.homeAway || 'away', published: !!o.published,
    tournament_id: o.tournamentId || '',
  });
  // Score (existing games): Sabres-first, never flipped for home/away.
  const sc0 = {
    our_score: score?.our_score ?? '', opp_score: score?.opp_score ?? '', is_final: !!score?.is_final,
    curr_period: score?.curr_period || '', curr_time: score?.curr_time || '', result: score?.result || '',
  };
  const [sc, setSc] = useState(sc0);
  // "On the website" starts from the game's real state (loaded with the score), and is only
  // sent when the scheduler changes it — saving a score must never unpublish a live game.
  const pub0 = score && score.published !== undefined ? !!score.published : !!o.published;
  useEffect(() => { setF(s => ({ ...s, published: pub0 })); }, [pub0]);
  const setS = (k) => (e) => setSc(x => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const autoResult = (sc.our_score !== '' && sc.opp_score !== '')
    ? `${sc.is_final ? `${+sc.our_score > +sc.opp_score ? 'W' : +sc.our_score < +sc.opp_score ? 'L' : 'T'}: ` : ''}${sc.our_score}-${sc.opp_score}` : '';
  const scoreChanged = Object.keys(sc0).filter(k => String(sc[k]) !== String(sc0[k]));
  const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));
  // A tournament game whose stand-in opponent becomes a real one goes on the website by
  // default (W on, pushed to WordPress on Save) — AJ 2026-10-18. Untick to keep it off.
  const pickOpponent = (name) => setF(s => ({
    ...s, opponent: name,
    published: (t && isStandIn(o.opponent, t, o.opponentSlug) && !isStandIn(name, t) && !s.slot_state) ? true : s.published,
  }));
  const to12 = (hhmm) => { if (!hhmm) return ''; const [h, m] = hhmm.split(':').map(Number); return minToLabel(h * 60 + m); };
  const ic = inputCls(isDark);
  return (
    <Shell title={o.isNew ? `Add ${o.teamLabel || ''} game` : 'Edit game'} onClose={onClose} isDark={isDark}>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <label className="space-y-1">Date<input type="date" className={ic} value={f.date} onChange={set('date')} disabled={readOnly} /></label>
        <label className="space-y-1">Tip-off (blank = TBD)<input type="time" className={ic} value={f.time} onChange={set('time')} disabled={readOnly} /></label>
        <label className="space-y-1 col-span-2">Opponent
          {/* Known opponents only, like the Schedule list (+ Add New opens the same Add Team dialog). */}
          <select className={ic} value={f.opponent} disabled={readOnly} data-testid="tw-opp"
            onChange={(e) => {
              if (e.target.value === ADD_OPP) { onAddOpponent?.((name) => pickOpponent(name)); return; }
              pickOpponent(e.target.value);
            }}>
            <option value="">{o.isNew ? '— choose opponent —' : 'Not known yet'}</option>
            {onAddOpponent && <option value={ADD_OPP}>+ Add New…</option>}
            {f.opponent && !(opponents || []).includes(f.opponent) && <option value={f.opponent}>{f.opponent} (current)</option>}
            {(opponents || []).map(n => <option key={n} value={n}>{n}</option>)}
          </select></label>
        <label className="space-y-1 col-span-2">Court
          <select className={ic} value={f.venue_slug} onChange={set('venue_slug')} disabled={readOnly}>
            <option value="">Court TBD</option>
            {f.venue_slug && !courts.some(v => v.slug === f.venue_slug) && (
              <option value={f.venue_slug}>{o.venue || f.venue_slug} (current)</option>)}
            {courts.map(v => <option key={v.slug} value={v.slug}>{v.name}</option>)}
          </select></label>
        <label className="space-y-1">Round<input className={ic} value={f.round} onChange={set('round')} placeholder="Pool A, Semifinal…" disabled={readOnly} /></label>
        <label className="space-y-1">Home / away
          <select className={ic} value={f.home_away} onChange={set('home_away')} disabled={readOnly}>
            <option value="home">Home (vs)</option><option value="away">Away (@)</option></select></label>
        <label className="space-y-1 col-span-2">State
          <select className={ic} value={f.slot_state} onChange={set('slot_state')} disabled={readOnly}>
            <option value="">Game</option><option value="held">Slot held (off the website)</option>
            <option value="if_win">Only if we win (off the website)</option></select></label>
        {/* In / out of a tournament by hand: a game taken out stays out (detection skips it). */}
        <label className="space-y-1 col-span-2">Tournament
          <select className={ic} value={f.tournament_id} onChange={set('tournament_id')} disabled={readOnly} data-testid="tw-tourn">
            <option value="">— not in a tournament —</option>
            {(tournaments || []).map(x => <option key={x.id} value={x.id}>🏆 {x.name}</option>)}
          </select></label>
        {!o.isNew && (
          <fieldset className={`col-span-2 rounded border px-2 pb-2 pt-1 ${isDark ? 'border-slate-600' : 'border-slate-200'}`} data-testid="tw-score">
            <legend className="px-1 font-semibold">Score {score === null && <span className="font-normal opacity-60">(loading…)</span>}</legend>
            <div className="grid grid-cols-3 gap-2 items-end">
              <label className="space-y-1">Our score<input type="number" min="0" className={ic} value={sc.our_score} onChange={setS('our_score')} disabled={readOnly || score === null} data-testid="tw-our" /></label>
              <label className="space-y-1">Opponent<input type="number" min="0" className={ic} value={sc.opp_score} onChange={setS('opp_score')} disabled={readOnly || score === null} data-testid="tw-opp-score" /></label>
              <label className="inline-flex items-center gap-1.5 pb-1.5"><input type="checkbox" checked={sc.is_final} onChange={setS('is_final')} disabled={readOnly || score === null} data-testid="tw-final" /> Final</label>
              {!sc.is_final && <>
                <label className="space-y-1">Period / set<input className={ic} value={sc.curr_period} onChange={setS('curr_period')} placeholder="2nd, Set 3…" disabled={readOnly || score === null} /></label>
                <label className="space-y-1 col-span-2">Time left<input className={ic} value={sc.curr_time} onChange={setS('curr_time')} placeholder="4:32" disabled={readOnly || score === null} /></label>
              </>}
              <label className="space-y-1 col-span-3">Result <span className="opacity-60">(blank = {autoResult || 'built from the score'}; add set scores in ( ), our side first)</span>
                <input className={ic} value={sc.result} onChange={setS('result')} placeholder={autoResult || 'W: 2-0 (25-17, 25-17)'} disabled={readOnly || score === null} data-testid="tw-result" /></label>
            </div>
          </fieldset>
        )}
        <label className="col-span-2 inline-flex items-center gap-2">
          <input type="checkbox" checked={f.published && !f.slot_state} disabled={readOnly || !!f.slot_state}
            onChange={(e) => setF(s => ({ ...s, published: e.target.checked }))} data-testid="tw-pub" />
          On the website{f.slot_state ? ' (held / if-win games stay off it)' : ''}</label>
      </div>
      {error && <div className="text-xs rounded px-2 py-1.5 bg-red-50 text-red-700 border border-red-200" data-testid="tw-error">{error}</div>}
      {!readOnly && (
        <div className="flex gap-2 pt-1">
          {!o.isNew && <button type="button" onClick={onDelete} className="text-xs text-red-600 hover:underline" data-testid="tw-delete">Delete game</button>}
          <span className="flex-1" />
          <button type="button" data-testid="tw-save" className="rounded bg-amber-600 text-white text-sm px-3 py-1.5"
            onClick={() => onSave({ date: f.date, time: to12(f.time), venue_slug: f.venue_slug,
              round: f.round, slot_state: f.slot_state, home_away: f.home_away,
              ...((o.isNew || f.published !== pub0) ? { published: f.published && !f.slot_state } : {}),
              ...((o.isNew || f.tournament_id !== (o.tournamentId || '')) ? { tournament_id: f.tournament_id } : {}),
              ...(f.opponent.trim() ? { opponent: f.opponent.trim() } : {}),
              // only the score fields that changed; an untouched result is rebuilt from the new
              // score on the server, keeping any set scores in ( )
              ...Object.fromEntries(scoreChanged.map(k => [k, sc[k]])) })}>Save</button>
        </div>
      )}
    </Shell>
  );
}

function TournamentEditor({ t, teamColumns, venues, isDark, sport, season, onClose, onSave, onTag, onDelete }) {
  const [f, setF] = useState({
    name: t.name || '', short_name: t.short_name || '', start_date: t.start_date || '', end_date: t.end_date || '',
    location: t.location || '', schedule_slugs: t.schedule_slugs || [], venue_slugs: t.venue_slugs || [],
  });
  const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));
  // Renaming carries the day-header short name along while it is still the old name's
  // first word (or empty) — a short name the scheduler typed is kept.
  const firstWord = (n) => (n || '').trim().split(/\s+/)[0] || '';
  const setName = (e) => {
    const name = e.target.value;
    setF(s => ({ ...s, name,
      short_name: (!s.short_name || s.short_name === firstWord(s.name)) ? firstWord(name) : s.short_name }));
  };
  const toggle = (k, v) => setF(s => ({ ...s, [k]: s[k].includes(v) ? s[k].filter(x => x !== v) : [...s[k], v] }));
  const ic = inputCls(isDark);
  const teams = (teamColumns || []).filter(c => c.schedule_slug);
  return (
    <Shell title={t.id ? 'Tournament' : 'New tournament'} onClose={onClose} isDark={isDark}>
      {t.auto && <div className="text-xs opacity-80">Found automatically: a team played the same opponent on back-to-back days, or 2+ games in one day in one place. Its games join and leave on their own. Type the tournament's real name below and Save.</div>}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <label className="space-y-1 col-span-2">Name<input className={ic} value={f.name} onChange={setName}
          placeholder="e.g. Gaitlinburg Homeschool Bash Nationals" data-testid="tw-tourn-name" /></label>
        <label className="space-y-1">Short name (day header)<input className={ic} value={f.short_name} onChange={set('short_name')} /></label>
        <label className="space-y-1">Where<input className={ic} value={f.location} onChange={set('location')} /></label>
        <label className="space-y-1">First day<input type="date" className={ic} value={f.start_date} onChange={set('start_date')} /></label>
        <label className="space-y-1">Last day<input type="date" className={ic} value={f.end_date} onChange={set('end_date')} /></label>
      </div>
      <div className="text-xs space-y-1"><div className="font-medium">Our teams entered</div>
        <div className="flex flex-wrap gap-2">{teams.map(c => (
          <label key={c.schedule_slug} className="inline-flex items-center gap-1">
            <input type="checkbox" checked={f.schedule_slugs.includes(c.schedule_slug)} onChange={() => toggle('schedule_slugs', c.schedule_slug)} />{c.label}</label>
        ))}</div></div>
      <div className="text-xs space-y-1"><div className="font-medium">Courts used (listed first when picking a court)</div>
        <select multiple className={`${ic} h-28`} value={f.venue_slugs}
          onChange={(e) => setF(s => ({ ...s, venue_slugs: [...e.target.selectedOptions].map(o => o.value) }))}>
          {venues.map(v => <option key={v.slug} value={v.slug}>{v.name}</option>)}
        </select></div>
      <div className="flex flex-wrap gap-2 pt-1 items-center">
        {t.id && <button type="button" onClick={onDelete} className="text-xs text-red-600 hover:underline" data-testid="tw-tourn-delete">
          {t.auto ? 'Not a tournament' : 'Delete tournament'}</button>}
        {t.id && !t.auto && <button type="button" onClick={onTag} className="text-xs rounded border px-2 py-1" data-testid="tw-tag">Add this tournament's games</button>}
        <span className="flex-1" />
        <button type="button" className="rounded bg-amber-600 text-white text-sm px-3 py-1.5" data-testid="tw-tourn-save"
          onClick={() => onSave({ ...f, sport, season })}>Save</button>
      </div>
    </Shell>
  );
}
